import { Link } from "react-router-dom";
import {
  Brain,
  ClipboardCheck,
  Pill,
  FileSignature,
  Stethoscope,
  Phone,
  ShieldCheck,
  Users,
  Sparkles,
  Check,
  ArrowRight,
  Activity,
} from "lucide-react";

const FEATURES = [
  {
    id: "ai-companion",
    icon: <Brain className="w-7 h-7" />,
    tag: "Patented",
    title: "AI Genius Companion",
    tagline: "An always-on, clinically-informed intelligence that meets you where you are.",
    description:
      "Our AI companion is trained on coping-skill protocols, crisis de-escalation, and trauma-informed care. It teaches you grounding techniques, helps you journal, tracks patterns, and — critically — knows when to bring a human provider into the conversation.",
    bullets: [
      "Coping skills on demand (DBT, CBT, grounding)",
      "Smart escalation to crisis specialists",
      "Pattern recognition across your journal & mood",
      "Warm handoff to your licensed provider",
    ],
  },
  {
    id: "insurance",
    icon: <ClipboardCheck className="w-7 h-7" />,
    tag: "pVerify",
    title: "Instant Insurance Verification",
    tagline: "No faxing. No front-desk hold music. No surprises.",
    description:
      "pVerify integration auto-verifies Medicaid, Medicare, and most commercial insurance in seconds. Patients know their coverage before their first session — and providers know they'll get paid.",
    bullets: [
      "Real-time eligibility & benefits check",
      "Medicaid + Medicare supported",
      "Commercial plans supported",
      "Transparent patient-cost estimates",
    ],
  },
  {
    id: "prescribing",
    icon: <Pill className="w-7 h-7" />,
    tag: "DoseSpot + Drug Bank",
    title: "E-Prescribing & Drug Intelligence",
    tagline: "Safer prescribing — and smarter patient education.",
    description:
      "Providers prescribe through DoseSpot's certified e-prescribing pipeline. Patients get plain-language Drug Bank information on every medication — side effects, interactions, and non-medication alternatives — all inside the app.",
    bullets: [
      "DoseSpot certified e-prescribing",
      "Drug Bank safety & interaction data",
      "Patient-friendly medication explainers",
      "Pharmacy integration",
    ],
  },
  {
    id: "docusign",
    icon: <FileSignature className="w-7 h-7" />,
    tag: "DocuSign",
    title: "Consent for Minors & Emancipated Youth",
    tagline: "Legally sound. Clinically sensitive. Built for families.",
    description:
      "Parental consent, guardian co-signing, and emancipated-youth self-consent flows — all handled through DocuSign. Clear, compliant, and respectful of both protection and autonomy.",
    bullets: [
      "Parent/guardian consent flows",
      "Emancipated youth self-consent",
      "State-specific compliance",
      "Audit-ready records",
    ],
  },
  {
    id: "backflow",
    icon: <Stethoscope className="w-7 h-7" />,
    tag: "Patented",
    title: "Provider Backflow",
    tagline: "A patented clinical workflow that respects your time.",
    description:
      "Our patented backflow routes the right patient signals to the right provider at the right moment — reducing clinician inbox overload, surfacing urgency, and keeping humans in the loop where humans are needed most.",
    bullets: [
      "Intelligent signal routing",
      "Urgency-aware triage",
      "Reduced clinician overload",
      "Audit trail for every touchpoint",
    ],
  },
  {
    id: "crisis",
    icon: <Phone className="w-7 h-7" />,
    tag: "24/7",
    title: "Crisis Support, Anytime",
    tagline: "Because crises don't keep business hours.",
    description:
      "Trained crisis specialists, peer supporters with lived experience, and licensed professionals — all reachable directly through the app. Matching happens in under two minutes, any hour of any day.",
    bullets: [
      "Crisis specialists available 24/7",
      "Certified peer support",
      "Licensed clinical professionals",
      "988 & 911 integration",
    ],
  },
  {
    id: "integrity",
    icon: <Activity className="w-7 h-7" />,
    tag: "Real-time",
    title: "Real-Time Integrity Monitoring",
    tagline: "Every interaction logged. Every claim verified. Zero room for fraud.",
    description:
      "A built-in metric system continuously tracks patient and provider activity across the platform — flagging anomalies, verifying session legitimacy, and protecting both insurance partners and the integrity of every clinical encounter. The result is a network where bad actors can't hide and good clinicians get paid faster.",
    bullets: [
      "Real-time anomaly detection across patients & providers",
      "Session-level audit trails for every encounter",
      "Provider activity verification & credentialing checks",
      "Automated suspicious-pattern flagging",
      "Insurance-grade compliance reporting",
      "Fraud, waste & abuse prevention by design",
    ],
  },
];

export default function Features() {
  return (
    <div data-testid="features-page" className="bg-white">
      {/* HERO */}
      <section className="py-20 lg:py-28 bg-gradient-hero" data-testid="features-hero">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow mb-5">The full stack</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-navy">
            Built like a <span className="hl-teal">clinic.</span><br />Feels like a friend.
          </h1>
          <p className="mt-7 text-lg sm:text-xl text-muted-ink leading-relaxed max-w-2xl mx-auto">
            Six integrated capabilities — from AI companionship to patented clinical workflow —
            all HIPAA-compliant, all working together inside one mobile app.
          </p>
          <div className="mt-9 inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-teal-light border border-teal/20">
            <ShieldCheck className="w-4 h-4 text-teal" />
            <span className="text-sm font-bold text-navy">HIPAA compliant by design</span>
          </div>
        </div>
      </section>

      {/* FEATURE DETAIL */}
      <section className="py-20 lg:py-28" data-testid="features-detail">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 space-y-24">
          {FEATURES.map((f, i) => {
            const reversed = i % 2 === 1;
            return (
              <div
                key={f.id}
                id={f.id}
                className={`grid lg:grid-cols-2 gap-10 lg:gap-20 items-center ${reversed ? "lg:[&>:first-child]:order-2" : ""}`}
                data-testid={`feature-${f.id}`}
              >
                <div className="relative">
                  <div className="aspect-[4/5] rounded-[2rem] bg-gradient-navy overflow-hidden relative p-10 flex flex-col justify-between">
                    <div className="absolute top-10 right-10 w-40 h-40 bg-teal/30 rounded-full blur-3xl" aria-hidden="true" />
                    <div className="absolute bottom-10 left-10 w-32 h-32 bg-teal/20 rounded-full blur-2xl" aria-hidden="true" />
                    <div className="relative flex items-start justify-between">
                      <div className="w-20 h-20 rounded-2xl bg-white/10 border border-white/20 text-teal flex items-center justify-center backdrop-blur">
                        {f.icon}
                      </div>
                      <span className="px-3 py-1 rounded-full bg-teal/20 text-teal text-xs font-bold uppercase tracking-widest">{f.tag}</span>
                    </div>
                    <div className="relative">
                      <p className="font-display text-6xl text-white/10 mb-3">0{i + 1}</p>
                      <h3 className="font-display text-4xl text-white leading-tight">{f.title}</h3>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="eyebrow mb-3">{f.tag}</p>
                  <h2 className="font-display text-4xl sm:text-5xl text-navy leading-[1.05] mb-4">{f.title}</h2>
                  <p className="text-xl text-teal-dark font-semibold mb-5">{f.tagline}</p>
                  <p className="text-lg text-muted-ink leading-relaxed mb-7">{f.description}</p>
                  <ul className="space-y-3">
                    {f.bullets.map((b, bi) => (
                      <li key={bi} className="flex items-start gap-3 text-navy font-medium">
                        <span className="mt-1 w-5 h-5 rounded-full bg-teal flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-white" />
                        </span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-cream" data-testid="features-cta">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <Sparkles className="w-10 h-10 text-teal mx-auto mb-5" />
          <h2 className="font-display text-4xl sm:text-5xl text-navy mb-5 leading-[1.1]">
            Ready to <span className="hl-teal">feel the difference?</span>
          </h2>
          <p className="text-lg text-muted-ink mb-8">
            Download Generational Health and take your first small step — right now.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/download" className="btn-primary" data-testid="features-download-cta">
              Get the app <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/for-providers" className="btn-outline" data-testid="features-provider-cta">
              <Users className="w-4 h-4" /> I'm a provider
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
